import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  type UserOrderResponseV2,
  type UserOrderResponseWithFlags,
  type UserOrderResponseWithFlagsSubOrdersInner,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { Button } from 'base-ui'

import { type ContactFormState } from '../../../../contact-form'
import { type OpenableControls } from '../../../../generic-hooks'
import { OrderSubOrderStatus } from '../../../../order'
import { priceGetFormattedPrice } from '../../../../price'
import { useDirectFormDialogContext } from '../DirectContactFormDialogContext'

import { DirectContactFormOrderSelectionCardImageThumbnail } from './DirectContactFormOrderSelectionCardImageThumbnail'

export const DirectContactFormOrderProductsSelectionCard: FC<{
  formState: ContactFormState
  modalControls: OpenableControls
  order: UserOrderResponseWithFlags | UserOrderResponseV2
}> = ({ formState, modalControls, order }) => {
  const intl = useIntl()
  const {
    setOrderSelectionDialogTitle,
    setProductList,
    setSelectedOrder,
    setSubOrderNumber,
  } = useDirectFormDialogContext()

  if (!order.orderMasterData?.orderNumber || !order.subOrders) {
    return null
  }

  const subOrder: UserOrderResponseWithFlagsSubOrdersInner | UserSubOrderV2 =
    order.subOrders[0]

  const productType = new Set([
    intl.formatMessage({ id: 'direct.contact.form.order.cancellation' }),
    intl.formatMessage({ id: 'direct.contact.form.report.order' }),
    intl.formatMessage({ id: 'direct.contact.form.return.order' }),
  ])

  if (!subOrder.status) {
    return null
  }

  return (
    <Button
      className="flex cursor-pointer items-center justify-between rounded-2xl bg-light-primary-low p-4 text-dark-primary-max shadow-m transition-all hover:bg-light-brand-medium hover:shadow-xl"
      data-qa-id="direct-contact-form-order-selection-card"
      onClick={() => {
        if (order.orderMasterData?.orderNumber) {
          setSelectedOrder(order)
          setProductList([])
          setSubOrderNumber(order.orderMasterData.orderNumber)
        }

        if (
          formState.model.subject &&
          productType.has(formState.model.subject)
        ) {
          setOrderSelectionDialogTitle(
            intl.formatMessage({
              id: 'direct.contact.form.select.product.title',
            }),
          )
        } else {
          modalControls.handleClose()
        }
      }}
    >
      <div className="w-full">
        <p className="mb-3 flex text-m font-medium">
          <FormattedMessage
            id="contact.ordersSection.card.title"
            values={{ date: intl.formatDate(order.orderMasterData.orderDate) }}
          />
        </p>
        <div className="flex w-full justify-between">
          <p className="mb-3 flex text-s font-medium">
            {order.subOrders[0].sellerName}
          </p>
          <p className="mb-3 items-end text-s font-medium">
            {order.orderMasterData.totalAmount === undefined ? null : (
              <span
                className="pr-1 text-center font-bold"
                data-qa-id="direct-contact-form-order-total-amount"
              >
                {priceGetFormattedPrice(
                  order.orderMasterData.totalAmount,
                  intl,
                )}
              </span>
            )}
          </p>
        </div>
        <OrderSubOrderStatus includeNowIndicator subOrder={subOrder} />
        <DirectContactFormOrderSelectionCardImageThumbnail order={order} />
      </div>
    </Button>
  )
}
